<template>
  <MarketCard class="win-win">
    <CardHeader :eventId="eventId" />

    <div :class="classes.eventMarketWrapper">
      <MarketName :marketId="marketId" />
    </div>

    <div :class="classes.optionContainer">
      <Banner
        :class="classes.banner"
        :badge="competitors[0].badge"
        :countryCode="competitors[0].colors.country_code_a2"
        :eventId="eventId"
        :useTeamNames="!useTeamIcons"
        :teamName="competitors[0].name.short"
        :isLeft="true"
        :disableBadge="!useTeamIcons"
        alignment="left"
        homeOrAwayTeam="home"
      />

      <div :class="classes.vs">
        <VersusIcon :eventId="eventId" />
      </div>

      <Banner
        :class="classes.banner"
        :badge="competitors[1].badge"
        :countryCode="competitors[1].colors.country_code_a2"
        :eventId="eventId"
        :useTeamNames="!useTeamIcons"
        :teamName="competitors[1].name.short"
        :disableBadge="!useTeamIcons"
        alignment="right"
        homeOrAwayTeam="away"
      />

      <div :class="classes.container">
        <div :class="[classes.competitorWrapper]">
          <WinCompetitor
            v-if="useTeamIcons"
            key="0"
            :class="classes.competitor"
            :team="competitors[0].name.short"
            :eventId="eventId"
          />
          <WinCompetitor
            v-if="useTeamIcons"
            key="1"
            :class="classes.competitor"
            :team="competitors[1].name.short"
            :eventId="eventId"
          />
        </div>
        <div :class="[classes.buttonWrapper]">
          <Button
            :stagger="0"
            :class="classes.button"
            :optionId="options[0].options[0]"
            :marketId="options[0].marketId"
          />

          <Button
            :stagger="1"
            :class="classes.button"
            :optionId="options[0].options[1]"
            :marketId="options[0].marketId"
          />
        </div>
      </div>
    </div>
  </MarketCard>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { defineComponent, ref, computed, ComputedRef } from "vue";

import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";

import {
  composeEventCompetitors,
  composeHomeAwayOptions
} from "@fresh8/ad-data-composition/src/betData";

import WinCompetitor from "../../../WinCompetitor.vue";
import CardHeader from "../../../CardHeader.vue";
import MarketName from "../../../MarketName.vue";
import VersusIcon from "../../../TeamVersusIcon.vue";
import MarketCard from "../../../styled/MarketCard.vue";
import Banner from "./Banner.vue";
import Button from "../Button.vue";

import {
  injectTeamColors,
  injectUseTeamBadges
} from "../../../../lib/teamBranding";
import { injectUseTeamJerseys } from "../../../../lib/teamJerseys";
import { SportCompetitor } from "@fresh8/types/src/betData";

const useStyles = createUseStyles({
  eventMarketWrapper: {
    position: "absolute",
    top: "21px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "16px 0 0px",

    "& span.market-name": {
      fontSize: "12px"
    }
  },

  competitor: {
    placeSelf: "center",
    fontSize: "11px",
    textAlign: "center",
    width: "100%"
  },

  optionContainer: {
    "--gridColumns": "repeat(3, 1fr) repeat(2, 8%) repeat(3, 1fr)",
    position: "relative",
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "var(--gridColumns)",
    gridAutoRows: "auto",
    gridTemplateRows: "100px auto",
    height: "100%",
    justifyContent: "space-evenly",
    lineHeight: 1,

    minHeight: "0",
    marginBottom: "10px",
    "&:after": {
      content: "''",
      position: "absolute",
      height: "calc(75% - 25px)",
      left: "50%",
      top: "40%",
      width: "0",
      transform: "translatex(-50%)",
      borderLeftWidth: "1px",
      borderLeftStyle: "solid",
      borderLeftColor: props => props.dividerColor
    }
  },

  banner: {
    gridColumn: "span 4",

    "&.left .badge.left": {
      left: "42%",
      transform: "translate(calc(-42%))"
    },
    "&.right .badge.right": {
      left: "58%",
      transform: "translate(calc(-58%))"
    },

    "&.left .jersey.left": {
      left: "42%",
      transform: "translate(calc(-42%))"
    },
    "&.right .jersey.right": {
      left: "58%",
      transform: "translate(calc(-58%))"
    },

    "& .team-jersey": {
      maxHeight: "34px"
    }
  },

  container: {
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "var(--gridColumns)",
    gridTemplateRows: "auto 1fr",
    gridRow: 2
  },

  competitorWrapper: {
    boxSizing: "border-box",
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "var(--gridColumns)",
    margin: "8px 10px",

    "& p": {
      gridColumn: "span 3",
      fontSize: "14px"
    },
    "& p:last-of-type": {
      gridColumn: "6 / span 3"
    }
  },
  buttonWrapper: {
    boxSizing: "border-box",
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "var(--gridColumns)",
    padding: "0 10px"
  },

  button: {
    gridColumn: "span 3",
    "&:last-of-type": {
      gridColumn: "6 / span 3"
    },

    "& div": {
      padding: "2px",
      margin: "0",
      width: "100%",

      "& .odds-text": {
        fontSize: "14px"
      },

      "& > span": {
        padding: "2px"
      }
    },

    "& .name": {
      display: "none"
    }
  },
  vs: {
    "--width": "41px",
    position: "absolute",
    left: "50%",
    top: "50px",
    transform: "translatex(-50%)",
    height: "var(--width)",
    Width: "var(--width)",
    zIndex: 1,
    "& svg": {
      maxHeight: "var(--width)",
      maxWidth: "var(--width)"
    }
  }
});

export default defineComponent({
  name: "LargeResponsiveWWV2",
  components: {
    CardHeader,
    Button,
    WinCompetitor,
    MarketName,
    VersusIcon,
    MarketCard,
    Banner
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const brandsContext = injectBrand() || ({} as BrandContext);
    const useTeamBadges = injectUseTeamBadges();
    const useTeamJerseys = injectUseTeamJerseys();
    const teamColorsContext = injectTeamColors();

    const competitors = composeEventCompetitors(props.eventId) as ComputedRef<
      SportCompetitor[]
    >;

    const useTeamIcons = computed(
      () =>
        (useTeamJerseys.value || useTeamBadges.value) &&
        teamColorsContext.teamIconsAvailable.value
    );

    const backgroundImage = computed(() => {
      return !!Object.keys(brandsContext?.backgroundImageSets?.value || {})
        .length;
    });

    const options = composeHomeAwayOptions(ref([props.marketId]));

    const relativeStyles = computed(() => ({
      ...(brandsContext?.adTemplateBranding.value || {}),
      backgroundImage: backgroundImage.value,
      dividerColor: brandsContext?.teamBranding.value.versusIcon.borderColor
    }));

    const classesRef = useStyles(relativeStyles);

    return {
      options,
      competitors,
      classes: classesRef,
      useTeamIcons
    };
  }
});
</script>
